/**
 * Single source of truth for the frame action set surfaced as 1-click
 * items in BOTH the floating frame toolbar and the right-click context
 * menu's frame branch. Adding an entry here lights it up in both places.
 *
 * Scope: 1-click frame actions only. Formatting (Fill/Border, Icon
 * picker) stays out of this list because its UX is fundamentally
 * different across surfaces — the toolbar uses popovers, the menu
 * doesn't have room for an embedded picker. Formatting belongs in
 * FrameFormattingToolbar; this list governs everything that fits the
 * "label + icon + click → run" shape.
 *
 * Shape note: actions don't read Yjs themselves. The two surfaces wire
 * doc-aware handlers (e.g. `(id) => collapseFrame(id)`) and pass them
 * through ctx. That keeps this file pure (no Yjs imports) and lets the
 * unit tests exercise run() with plain spies.
 */

export interface FrameActionContext {
  nodeId: string;
  locked: boolean;
  collapsed: boolean;
  onEditTitle: (id: string) => void;
  onToggleCollapsed: (id: string) => void;
  onUngroup: (id: string) => void;
  onToggleLock: (id: string) => void;
  onDelete: (id: string) => void;
}

export interface FrameAction {
  /** Stable identifier used by tests + by surfaces that need to skip a
   *  specific action (rare — most surfaces just render the whole list
   *  filtered by available()). */
  id: 'edit-title' | 'toggle-collapse' | 'ungroup' | 'lock' | 'delete';
  /** Plain label shown in the menu and as the toolbar IconButton's
   *  aria-label / tooltip. Toggle-collapse provides a function so the
   *  label flips between "Open frame" / "Close frame" based on state. */
  label: string;
  /** Material Symbols icon name. Same name in both surfaces. Toggle-
   *  collapse swaps icons via `iconFor(ctx)` so the affordance reads
   *  correctly in either state. */
  icon: string;
  /** Optional keyboard shortcut display string (menu only). */
  shortcut?: string;
  /** Mark the menu item as destructive (red-tinted). Toolbar treats
   *  this as a hint for the IconButton variant. */
  danger?: boolean;
  /** State-dependent overrides for label/icon — used by
   *  toggle-collapse so the same registry entry covers both states. */
  labelFor?: (ctx: FrameActionContext) => string;
  iconFor?: (ctx: FrameActionContext) => string;
  /** Whether the action should appear in the surface for this context.
   *  Locked frames hide most entries — only Lock (now an Unlock
   *  affordance) and Delete survive, matching the existing locked-
   *  state toolbar UX. */
  available: (ctx: FrameActionContext) => boolean;
  /** Imperative trigger. Calls the matching ctx handler with nodeId. */
  run: (ctx: FrameActionContext) => void;
}

export const FRAME_ACTIONS: FrameAction[] = [
  {
    id: 'edit-title',
    label: 'Edit title',
    icon: 'text_fields',
    available: (c) => !c.locked,
    run: (c) => c.onEditTitle(c.nodeId),
  },
  {
    id: 'toggle-collapse',
    // Static label is "Toggle frame" so the registry entry stays stable;
    // the surfaces use labelFor()/iconFor() to render the state-aware
    // affordance ("Close frame" when open, "Open frame" when collapsed).
    label: 'Toggle frame',
    icon: 'unfold_less',
    labelFor: (c) => (c.collapsed ? 'Open frame' : 'Close frame'),
    iconFor: (c) => (c.collapsed ? 'unfold_more' : 'unfold_less'),
    available: (c) => !c.locked,
    run: (c) => c.onToggleCollapsed(c.nodeId),
  },
  {
    id: 'lock',
    // Locked → menu shows "Unlock"; unlocked → "Lock". Surfaces choose
    // their own label/icon logic (the toolbar uses HoldToUnlockButton
    // for locked frames so it never renders this entry as Unlock), so
    // labelFor/iconFor cover the menu's needs.
    label: 'Lock',
    icon: 'lock',
    labelFor: (c) => (c.locked ? 'Unlock' : 'Lock'),
    iconFor: (c) => (c.locked ? 'lock_open' : 'lock'),
    // Always available — locked frames need Unlock; unlocked need Lock.
    available: () => true,
    run: (c) => c.onToggleLock(c.nodeId),
  },
  {
    // Sits after Lock so the destructive cluster reads top-down as
    // "lock → ungroup → delete" — escalating-impact order.
    id: 'ungroup',
    label: 'Ungroup',
    // call_split reads as "break apart" — two arrows splitting
    // outward. Stronger metaphor than the previous `group_off`
    // negation slash, which read as "group disabled."
    icon: 'call_split',
    available: (c) => !c.locked,
    run: (c) => c.onUngroup(c.nodeId),
  },
  {
    id: 'delete',
    label: 'Delete',
    icon: 'delete',
    danger: true,
    available: () => true,
    run: (c) => c.onDelete(c.nodeId),
  },
];
